import { Status } from "../enums/status";

/** Clases del punto de color, compartidas por todas las grids de estado. */
export const STATUS_DOT_CLASS = {
	orange: "content-visibility-circle-orange",
	green: "content-visibility-circle-green",
	red: "content-visibility-circle-red",
	grey: "content-visibility-circle-grey",
} as const;

export type StatusDotClass =
	(typeof STATUS_DOT_CLASS)[keyof typeof STATUS_DOT_CLASS];

export const LIFECYCLE_STATUS_DOT_CLASSES: Readonly<Record<string, string>> = {
	PENDING: STATUS_DOT_CLASS.orange,
	ACTIVE: STATUS_DOT_CLASS.green,
	REGISTERED: STATUS_DOT_CLASS.green,
	CANCELED: STATUS_DOT_CLASS.red,
	DELETED: STATUS_DOT_CLASS.red,
};

export const STATUS_TEXT_STYLE = {
	dark: { color: "#000000", fontSize: "14px", fontWeight: "500" },
	compact: { fontSize: "14px", fontWeight: "500" },
} as const;

export type StatusTextStyle = Partial<
	Record<"color" | "fontSize" | "fontWeight", string>
>;

export interface StatusDotOptions {
	dotClass: string;
	label: string;
	textStyle?: StatusTextStyle;
}

/** Construye el chip "punto de color + texto" */
export function buildStatusDot({
	dotClass,
	label,
	textStyle,
}: StatusDotOptions): HTMLElement {
	const wrapperElement = document.createElement("div");
	wrapperElement.classList.add("visibility-with-dot");

	const dotElement = document.createElement("i");
	dotElement.classList.add("mdi", "mdi-circle", "content-circle", dotClass);

	const textElement = document.createElement("span");
	textElement.textContent = label;

	if (textStyle?.color) textElement.style.color = textStyle.color;
	if (textStyle?.fontSize) textElement.style.fontSize = textStyle.fontSize;
	if (textStyle?.fontWeight)
		textElement.style.fontWeight = textStyle.fontWeight;

	wrapperElement.appendChild(dotElement);
	wrapperElement.appendChild(textElement);
	return wrapperElement;
}

export function renderStatusDot(
	container: HTMLElement,
	options: StatusDotOptions & { clearContainer?: boolean },
): void {
	if (options.clearContainer) container.innerHTML = "";
	container.appendChild(buildStatusDot(options));
}

export interface StatusChipCellTemplateOptions {
	data: {
		status: Status | string;
		translatedStatus: string;
	};
	value?: string;
}

const REQUEST_STATUS_DOT_CLASSES: Partial<Record<Status, string>> = {
	[Status.PENDING]: STATUS_DOT_CLASS.orange,
	[Status.APPROVED]: STATUS_DOT_CLASS.green,
	[Status.DENIED]: STATUS_DOT_CLASS.red,
	[Status.OUTDATED]: STATUS_DOT_CLASS.grey,
};

export function renderStatusChip(
	container: HTMLElement,
	options: StatusChipCellTemplateOptions,
): void {
	const dotClass = REQUEST_STATUS_DOT_CLASSES[options.data.status as Status];

	if (!dotClass) {
		container.textContent = options.data.translatedStatus ?? "";
		return;
	}

	renderStatusDot(container, {
		dotClass,
		label: options.data.translatedStatus,
	});
}
